/**
 * #53 PR 3 / DEC-360 — the Map View's Drivers panel (design §5b). Closed by default, so
 * the map is unchanged until it is opened. One row per driver currently on the map, with:
 *
 *   - an eye: show or hide THAT driver's pickup, disposal and route. The truck pin stays
 *     (C2). Only meaningful while a layer is on, so it is disabled, with a hint, otherwise.
 *   - Focus: overlays for the focused driver(s) only, other pins dimmed (C3). Works with
 *     layers off too, because dimming the other trucks is useful by itself.
 *
 * Nothing here is remembered (C4); DriverMap holds the state for this page only.
 * Presentational: no data fetching, no map access.
 */
import * as React from 'react';
import { Eye, EyeOff, X } from 'lucide-react';
import type { DriverPosition } from '@lodex-pro/shared';
import { presentCounts, type DriverOverlayState } from './driver-overlay-filter.js';

export interface DriversPanelProps {
  readonly positions: ReadonlyArray<DriverPosition>;
  /** Already pruned to the drivers on the map (`pruneToPresent`). */
  readonly state: DriverOverlayState;
  /** Whether any map layer is on — the eye does nothing visible otherwise. */
  readonly layersOn: boolean;
  /** The driver's pin colour, so the row matches the map. */
  readonly colorOf: (p: DriverPosition) => string;
  readonly onToggleHidden: (driverId: string) => void;
  readonly onToggleFocus: (driverId: string) => void;
  readonly onShowAll: () => void;
  readonly onClose: () => void;
}

const smallBtn: React.CSSProperties = {
  minWidth: '28px',
  height: '26px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 6px',
  border: '1px solid var(--color-border-primary)',
  borderRadius: 'var(--radius-pill)',
  backgroundColor: 'transparent',
  color: 'var(--color-text-primary)',
  fontSize: '11px',
  cursor: 'pointer',
};

export function DriversPanel({
  positions,
  state,
  layersOn,
  colorOf,
  onToggleHidden,
  onToggleFocus,
  onShowAll,
  onClose,
}: DriversPanelProps): React.JSX.Element {
  const rows = React.useMemo(
    () => [...positions].sort((a, b) => a.driverName.localeCompare(b.driverName)),
    [positions],
  );
  const counts = presentCounts(positions, state);

  let status: string | null = null;
  if (counts.focused > 0) status = `Focusing ${String(counts.focused)}`;
  else if (counts.hidden > 0) status = `${String(counts.hidden)} hidden`;

  return (
    <aside
      aria-label="Drivers"
      style={{
        position: 'absolute',
        top: '32px',
        left: '8px',
        width: '250px',
        maxHeight: 'calc(100% - 80px)',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border-primary)',
        borderRadius: '4px',
        fontSize: '12px',
        fontFamily: 'var(--font-sans)',
        color: 'var(--color-text-primary)',
        zIndex: 11,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '6px 8px',
          borderBottom: '1px solid var(--color-border-primary)',
        }}
      >
        <strong style={{ flex: 1 }}>Drivers</strong>
        {status !== null ? (
          <span role="status" style={{ color: 'var(--color-text-secondary)' }}>
            {status} ·{' '}
            <button
              type="button"
              onClick={onShowAll}
              style={{
                border: 'none',
                background: 'none',
                padding: 0,
                color: 'var(--color-accent-blue)',
                cursor: 'pointer',
                fontSize: '12px',
              }}
            >
              Show all
            </button>
          </span>
        ) : null}
        <button type="button" aria-label="Close drivers" onClick={onClose} style={smallBtn}>
          <X size={12} />
        </button>
      </div>

      {!layersOn ? (
        <p style={{ margin: 0, padding: '6px 8px', color: 'var(--color-text-secondary)' }}>
          Turn on a layer to see routes and sites. Focus still dims the other trucks.
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p style={{ margin: 0, padding: '8px', color: 'var(--color-text-secondary)' }}>
          No drivers on the map
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: 0, padding: '4px 0', overflowY: 'auto' }}>
          {rows.map((p) => {
            const hidden = state.hidden.has(p.driverId);
            const focused = state.focused.has(p.driverId);
            return (
              <li
                key={p.driverId}
                style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '3px 8px' }}
              >
                <span
                  aria-hidden="true"
                  style={{
                    width: '9px',
                    height: '9px',
                    borderRadius: '50%',
                    flexShrink: 0,
                    backgroundColor: colorOf(p),
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {p.driverName}
                  {p.truckNumber != null && p.truckNumber !== '' ? (
                    <span style={{ color: 'var(--color-text-secondary)' }}> · {p.truckNumber}</span>
                  ) : null}
                </span>
                <button
                  type="button"
                  aria-label={`${hidden ? 'Show' : 'Hide'} ${p.driverName}'s sites and route`}
                  aria-pressed={hidden}
                  disabled={!layersOn}
                  onClick={(): void => {
                    onToggleHidden(p.driverId);
                  }}
                  style={{ ...smallBtn, opacity: layersOn ? 1 : 0.4 }}
                >
                  {hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
                <button
                  type="button"
                  aria-label={`Focus on ${p.driverName}`}
                  aria-pressed={focused}
                  onClick={(): void => {
                    onToggleFocus(p.driverId);
                  }}
                  style={{
                    ...smallBtn,
                    backgroundColor: focused ? 'var(--color-accent-blue)' : 'transparent',
                    color: focused ? 'var(--color-on-accent)' : 'var(--color-text-primary)',
                  }}
                >
                  Focus
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </aside>
  );
}
